import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftSelect from 'components/SoftSelect';
import FormField from 'layouts/applications/wizard/components/FormField';
import DataTable from 'prefabsComponents/Tables/DataTable';
import Swal from 'sweetalert2';
import axios from 'axios';

// Datos iniciales para la tabla (se mantienen igual si no se hace mapeo de transporte aquí)
const initialTableData = {
    columns: [
        { Header: 'Descripción', accessor: 'descripcion' },
        { Header: 'Fecha de entrega', accessor: 'fecha_entrega' },
        { Header: 'Tipo de embalaje', accessor: 'tipo_embalaje' },
        { Header: 'Comprador', accessor: 'comprador' },
        { Header: 'Tipo de despacho', accessor: 'tipo_despacho' }, // Se renombra la columna
        { Header: 'País de origen', accessor: 'pais_origen' },
        { Header: 'Incoterms', accessor: 'incoterms' },
        { Header: 'Lugar de entrega', accessor: 'lugar_entrega' },
        { Header: 'Cantidad de unidades', accessor: 'cantidad_unidades' },
        { Header: 'Término de pago', accessor: 'termino_pago' },
        { Header: 'Tipo de moneda', accessor: 'tipo_moneda' },
        { Header: 'Precio', accessor: 'precio' },
        { Header: 'Acciones', accessor: 'acciones' },
    ],
    rows: [],
};

function OrderEmbarques() {
    // Estado del formulario. Se cambia "transporte" por "tipo_despacho"
    const [formData, setFormData] = useState({
        descripcion: '',
        fecha_entrega: '',
        tipo_embalaje: null,
        comprador: '',
        tipo_despacho: null,  // <-- Campo actualizado
        pais_origen: '',
        incoterms: null,
        lugar_entrega: '',
        cantidad_unidades: '',
        termino_pago: '',
        tipo_moneda: null,
        precio: '',
    });

    const getOptionLabel = (options, id) => {
        const option = options.find((opt) => opt.value === id);
        return option ? option.label : id;
    };

    const [tableData, setTableData] = useState(() => {
        const savedTableData = localStorage.getItem('embarqueTableData');
        return savedTableData ? JSON.parse(savedTableData) : initialTableData;
      });
    const [editingRowId, setEditingRowId] = useState(null);

    // Opciones para Tipo de despacho (antes transportOptions)
    const tipoDespachoOptions = [
        { value: 1, label: 'Terrestre' },
        { value: 2, label: 'Marítimo' },
        { value: 3, label: 'Aéreo' },
    ];

    // Opciones restantes (incoterms, embalaje, moneda) se mantienen sin cambios
    const [incotermsOptions, setIncotermsOptions] = useState([]);
    const [packagingOptions, setPackagingOptions] = useState([]);
    const [currencyOptions, setCurrencyOptions] = useState([]);

    useEffect(() => {
        localStorage.setItem('embarqueTableData', JSON.stringify(tableData));
        localStorage.setItem('embarqueFormData', JSON.stringify(formData));
      }, [tableData, formData]);    

    // Cargar opciones desde la API usando axios
    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_SERVER_URL}/api/maintenance/incoterms/`)
            .then((response) => {
                const options = response.data.map((item) => ({
                    value: item.id_incoterm,
                    label: item.nombre_incoterm,
                }));
                setIncotermsOptions(options);
            })
            .catch((err) => console.error('Error al cargar incoterms:', err));

        axios
            .get(`${process.env.REACT_APP_SERVER_URL}/api/maintenance/packaging-types/`)
            .then((response) => {
                const options = response.data.map((item) => ({
                    value: item.id_tipo_embalaje,
                    label: item.nombre_tipo_embalaje,
                }));
                setPackagingOptions(options);
            })
            .catch((err) => console.error('Error al cargar tipos de embalaje:', err));

        axios
            .get(`${process.env.REACT_APP_SERVER_URL}/api/finance/currency-types/`)
            .then((response) => {
                const options = response.data.results.map((item) => ({
                    value: item.id,
                    label: item.name,
                }));
                setCurrencyOptions(options);
            })
            .catch((err) => console.error('Error al cargar tipos de moneda:', err));
    }, []);

    // ... (la lógica de localStorage y manejo de inputs se mantiene igual, solo se reemplaza "transporte" por "tipo_despacho")

    // Manejar cambios en los inputs de texto y fecha
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        console.log("Nuevo formData:", { ...formData, [name]: value });
      };      

    // Manejar cambios en los SoftSelect: se recibe la opción seleccionada (objeto { value, label })
    const handleSelectChange = (name, selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            [name]: selectedOption || null,
        }));
    };

    // Función para guardar un nuevo registro
    const handleSaveNewRecord = () => {
        // Verificar que ninguno de los campos esté vacío
        // Se extraen los id de los selects para validar (si existen)
        const requiredFields = {
            ...formData,
            tipo_embalaje: formData.tipo_embalaje ? formData.tipo_embalaje.value : '',
            tipo_despacho: formData.tipo_despacho ? formData.tipo_despacho.value : '',  // validación actualizada
            incoterms: formData.incoterms ? formData.incoterms.value : '',
            tipo_moneda: formData.tipo_moneda ? formData.tipo_moneda.value : '',
        };
        if (Object.values(requiredFields).some((field) => field === '')) {
            Swal.fire({
                icon: 'error',
                title: 'Formulario incompleto',
                text: 'Por favor, completa todos los campos antes de guardar.',
            });
            return;
        }
        const newRowId = new Date().getTime();
        // Al guardar, se extraen los id de los selects
        const newRow = {
            ...formData,
            id: newRowId,
            tipo_embalaje: formData.tipo_embalaje.value,
            tipo_despacho: formData.tipo_despacho.value,  // se guarda el valor de tipo_despacho
            incoterms: formData.incoterms.value,
            tipo_moneda: formData.tipo_moneda.value,
        };

        setTableData((prev) => ({
            ...prev,
            rows: [...prev.rows, newRow],
        }));

        Swal.fire('Registro guardado', 'El embarque ha sido guardado exitosamente.', 'success');

        // Limpiar el formulario
        setFormData({
            descripcion: '',
            fecha_entrega: '',
            tipo_embalaje: null,
            comprador: '',
            tipo_despacho: null,
            pais_origen: '',
            incoterms: null,
            lugar_entrega: '',
            cantidad_unidades: '',
            termino_pago: '',
            tipo_moneda: null,
            precio: '',
        });
    };

    // Función para editar una fila
    const handleEditRow = (rowId) => {
        const rowToEdit = tableData.rows.find((row) => row.id === rowId);
        if (rowToEdit) {
            setFormData({
                ...rowToEdit,
                tipo_embalaje: packagingOptions.find((o) => o.value === rowToEdit.tipo_embalaje) || null,
                tipo_despacho: tipoDespachoOptions.find((o) => o.value === rowToEdit.tipo_despacho) || null,  // actualizamos
                incoterms: incotermsOptions.find((o) => o.value === rowToEdit.incoterms) || null,
                tipo_moneda: currencyOptions.find((o) => o.value === rowToEdit.tipo_moneda) || null,
            });
            setEditingRowId(rowId);
        }
    };

    // Función para actualizar un registro editado
    const handleUpdateRecord = () => {
        if (editingRowId) {
            setTableData((prev) => {
                const updatedRows = prev.rows.map((row) =>
                    row.id === editingRowId
                        ? {
                            ...formData,
                            id: editingRowId,
                            tipo_embalaje: formData.tipo_embalaje.value,
                            tipo_despacho: formData.tipo_despacho.value,  // se actualiza
                            incoterms: formData.incoterms.value,
                            tipo_moneda: formData.tipo_moneda.value,
                        }
                        : row
                );
                return { ...prev, rows: updatedRows };
            });
            setEditingRowId(null);
            Swal.fire('Registro actualizado', 'El embarque ha sido actualizado exitosamente.', 'success');
            setFormData({
                descripcion: '',
                fecha_entrega: '',
                tipo_embalaje: null,
                comprador: '',
                tipo_despacho: null,
                pais_origen: '',
                incoterms: null,
                lugar_entrega: '',
                cantidad_unidades: '',
                termino_pago: '',
                tipo_moneda: null,
                precio: '',
            });
        }
    };

    // Función para eliminar una fila
    const handleDeleteRow = (rowId) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: '¡No podrás revertir esto!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminarlo',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                setTableData((prev) => ({
                    ...prev,
                    rows: prev.rows.filter((row) => row.id !== rowId),
                }));
                Swal.fire('Eliminado!', 'La fila ha sido eliminada.', 'success');
            }
        });
    };

    // Función para renderizar botones de acción en la tabla
    const renderActionButtons = (rowId) => (
        <div>
            <Tooltip title="Editar" placement="top">
                <IconButton color="info" onClick={() => handleEditRow(rowId)}>
                    <EditIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar" placement="top">
                <IconButton color="error" onClick={() => handleDeleteRow(rowId)}>
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </div>
    );

    // Generar filas para la tabla, transformando los id a nombres mediante getOptionLabel
    const rowsWithActions = tableData.rows.map((row) => ({
        ...row,
        incoterms: getOptionLabel(incotermsOptions, row.incoterms),
        tipo_embalaje: getOptionLabel(packagingOptions, row.tipo_embalaje),
        tipo_despacho: getOptionLabel(tipoDespachoOptions, row.tipo_despacho),  // actualizamos aquí
        tipo_moneda: getOptionLabel(currencyOptions, row.tipo_moneda),
        acciones: renderActionButtons(row.id),
    }));

    return (
        <SoftBox>
            <SoftBox width="80%" textAlign="center" mx="auto" mb={4}>
                <SoftBox mb={1}>
                    <SoftTypography variant="h5" fontWeight="regular">
                        Este paso es opcional, aquí puedes ingresar los embarques que ya existan en la orden
                    </SoftTypography>
                </SoftBox>
            </SoftBox>
            <SoftBox>
                <SoftBox mt={2}>
                    <Grid container spacing={3}>
                        {/* Primera columna */}
                        <Grid item xs={12} md={3}>
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Descripción
                            </SoftTypography>
                            <FormField
                                type="text"
                                placeholder="Ingrese la descripción"
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleInputChange}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Fecha de entrega
                            </SoftTypography>
                            <FormField
                                type="date"
                                name="fecha_entrega"
                                value={formData.fecha_entrega}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Tipo de embalaje
                            </SoftTypography>
                            <SoftSelect
                                name="tipo_embalaje"
                                value={formData.tipo_embalaje}
                                onChange={(option) => handleSelectChange('tipo_embalaje', option)}
                                options={packagingOptions}
                                placeholder="Seleccione el tipo de embalaje"
                                menuPortalTarget={document.body}
                            />
                        </Grid>
                        {/* Segunda columna */}
                        <Grid item xs={12} md={3}>
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Comprador
                            </SoftTypography>
                            <FormField
                                type="text"
                                placeholder="Ingrese el comprador"
                                name="comprador"
                                value={formData.comprador}
                                onChange={handleInputChange}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Tipo de despacho
                            </SoftTypography>
                            <SoftSelect
                                name="tipo_despacho"
                                value={formData.tipo_despacho}
                                onChange={(option) => handleSelectChange('tipo_despacho', option)}
                                options={tipoDespachoOptions}
                                placeholder="Seleccione el tipo de despacho"
                                menuPortalTarget={document.body}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                País de origen
                            </SoftTypography>
                            <FormField
                                type="text"
                                placeholder="Ingrese el país"
                                name="pais_origen"
                                value={formData.pais_origen}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        {/* Tercera columna */}
                        <Grid item xs={12} md={3}>
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Incoterms
                            </SoftTypography>
                            <SoftSelect
                                name="incoterms"
                                value={formData.incoterms}
                                onChange={(option) => handleSelectChange('incoterms', option)}
                                options={incotermsOptions}
                                placeholder="Seleccione los incoterms"
                                menuPortalTarget={document.body}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Lugar de entrega
                            </SoftTypography>
                            <FormField
                                type="text"
                                placeholder="Ingrese el lugar de entrega"
                                name="lugar_entrega"
                                value={formData.lugar_entrega}
                                onChange={handleInputChange}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Cantidad de unidades
                            </SoftTypography>
                            <FormField
                                type="number"
                                placeholder="Ingrese la cantidad"
                                name="cantidad_unidades"
                                value={formData.cantidad_unidades}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        {/* Cuarta columna */}
                        <Grid item xs={12} md={3}>
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Término de pago
                            </SoftTypography>
                            <FormField
                                type="text"
                                placeholder="Ingrese los términos de pago"
                                name="termino_pago"
                                value={formData.termino_pago}
                                onChange={handleInputChange}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Tipo de moneda
                            </SoftTypography>
                            <SoftSelect
                                name="tipo_moneda"
                                value={formData.tipo_moneda}
                                onChange={(option) => handleSelectChange('tipo_moneda', option)}
                                options={currencyOptions}
                                placeholder="Seleccione el tipo de moneda"
                                menuPortalTarget={document.body}
                            />
                            <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Precio
                            </SoftTypography>
                            <FormField
                                type="text"
                                placeholder="Ingrese el precio"
                                name="precio"
                                value={formData.precio}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    </Grid>
                    <SoftBox mt={4} textAlign="center">
                        <Tooltip title="Limpiar Campos" placement="top">
                            <IconButton
                                color="warning"
                                onClick={() =>
                                    setFormData({
                                        descripcion: '',
                                        fecha_entrega: '',
                                        tipo_embalaje: null,
                                        comprador: '',
                                        tipo_despacho: null,
                                        pais_origen: '',
                                        incoterms: null,
                                        lugar_entrega: '',
                                        cantidad_unidades: '',
                                        termino_pago: '',
                                        tipo_moneda: null,
                                        precio: '',
                                    })
                                }
                            >
                                <ClearIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Guardar Registro" placement="top">
                            <IconButton color="success" onClick={handleSaveNewRecord}>
                                <ArrowDownwardIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Actualizar Registro" placement="top">
                            <IconButton color="info" onClick={handleUpdateRecord} disabled={!editingRowId}>
                                <RotateLeftIcon />
                            </IconButton>
                        </Tooltip>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
            <SoftBox mt={2}>
                <DataTable
                    table={{
                        ...tableData,
                        rows: tableData.rows.map((row) => ({
                            ...row,
                            incoterms: getOptionLabel(incotermsOptions, row.incoterms),
                            tipo_embalaje: getOptionLabel(packagingOptions, row.tipo_embalaje),
                            tipo_despacho: getOptionLabel(tipoDespachoOptions, row.tipo_despacho),
                            tipo_moneda: getOptionLabel(currencyOptions, row.tipo_moneda),
                            acciones: renderActionButtons(row.id),
                        })),
                    }}
                    entriesPerPage={false}
                    canSearch={false}
                />
            </SoftBox>
        </SoftBox>
    );
}

// Para que las funciones de editar y eliminar sean accesibles en el ámbito global,
// se definen en un ámbito superior y luego se reasignan dentro del componente.
let handleEditRow, handleDeleteRow;
export default OrderEmbarques;
